import React, { FC, FormEventHandler, useId, useRef, useState } from 'react';

import {
  LABEL__COLOR_DEFAULT,
  LABEL__COLOR_REQUIRED,
  LABEL__MAX_TEXT_LENGTH,
  LABEL__MIN_TEXT_LENGTH,
  LABEL__TEXT_REQUIRED,
  LABEL__TEXT_UNIQUE,
} from '../../../constants/forms';
import { Label } from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import PickerColor from '../../Common/PickerColor';

import TextInput from '../../MUI/TextInput';

import * as Styled from './styled';

export type Props = {
  ariaDescribedBy: string;
  ariaLabelledBy: string;
  dataColor: string;
  dataText: string;
  dataTextSelected?: string;
  idForm: string;
  labelsExisting: Label['text'][];
  requestChangeColor: (color: string) => void;
  requestChangeText: (text: string) => void;
  requestSave: () => void;
};

const nameColor = 'color';
const nameText = 'text';

/**
 * Form for updating a single label
 *
 * @param props                    Props passed to the component
 * @param props.ariaDescribedBy    Accessible form description
 * @param props.ariaLabelledBy     Accessible form title
 * @param props.dataColor          Label color
 * @param props.dataText           Label text
 * @param props.dataTextSelected   Initial label text which is currently selected
 * @param props.idForm             ID to use for the label update <form /> element
 * @param props.labelsExisting     Labels already present
 * @param props.requestChangeColor Request that the color is changed
 * @param props.requestChangeText  Request that the text is changed
 * @param props.requestSave        Request that the updated data is saved
 * @returns                        The component itself
 */
const LabelUpdateForm: FC<Props> = ({
  ariaDescribedBy,
  ariaLabelledBy,
  dataColor,
  dataText,
  dataTextSelected = '',
  idForm,
  labelsExisting,
  requestChangeColor,
  requestChangeText,
  requestSave,
}) => {
  const textInputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);

  const placeholder = translate('LABELS__ENTER_LABEL_NAME');
  const labelColor = translate('LABELS__COLOR__LABEL');
  const labelTitle = translate('LABEL__NAME');

  const isTextDuplicate =
    dataText !== dataTextSelected && labelsExisting.includes(dataText) === true;

  /**
   * Set text value and check if save button should be disabled
   *
   * @param value New value of text input
   */
  const handleTextChange = (value: string): void => {
    requestChangeText(value);

    if (LABEL__TEXT_UNIQUE === true) {
      // We check here if the current value of label.text is unique among all labels.
      // If that's not the case we show an error message and the form is marked as invalid.
      const errorMessage = labelsExisting.includes(value)
        ? translate('LABELS__ERROR__DUPLICATE__TITLE')
        : '';

      inputRef.current?.setCustomValidity(errorMessage);
    }
  };

  /**
   * The form was submitted (perhaps by pressing enter within an input)
   *
   * @param event The event that took place
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    setShouldValidate(true);

    if (event.currentTarget.checkValidity() === false) {
      return;
    }

    requestSave();
    setShouldValidate(false);
  };

  /**
   * Get the error state of the input
   *
   * @returns Whether the input has an error
   */
  const getInputError = () => {
    if (!shouldValidate) {
      return false;
    }
    if (dataText.length < LABEL__MIN_TEXT_LENGTH) {
      return true;
    }
    return isTextDuplicate;
  };

  /**
   * Get the error message for the input
   *
   * @returns The error message for the input
   */
  const getInputErrorMessage = () => {
    if (!shouldValidate) {
      return '';
    }

    if (dataText.length === 0) {
      return translate('GENERAL__FILL_FIELD');
    }

    if (dataText.length < 3) {
      /**
       * @todo Replace with a translation when BE is ready
       */
      return 'Label length must be at least 3 characters long';
    }

    if (isTextDuplicate) {
      return translate('LABELS__ERROR__DUPLICATE__TITLE');
    }

    return '';
  };

  const error = getInputError();
  const helperText = getInputErrorMessage();

  return (
    <Styled.Form
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
      id={idForm}
      noValidate={true}
      onSubmit={onSubmit}
    >
      <PickerColor
        isRequired={LABEL__COLOR_REQUIRED}
        label={labelColor}
        name={nameColor}
        onChange={requestChangeColor}
        placeholder={LABEL__COLOR_DEFAULT}
        value={dataColor}
      />
      <TextInput
        externalError={error}
        externalHelperText={helperText}
        id={textInputId}
        inputRef={inputRef}
        label={labelTitle}
        maxLength={LABEL__MAX_TEXT_LENGTH}
        minLength={LABEL__MIN_TEXT_LENGTH}
        name={nameText}
        onChange={handleTextChange}
        placeholder={placeholder}
        required={LABEL__TEXT_REQUIRED}
        shouldFocus={true}
        type="text"
        value={dataText}
      />
    </Styled.Form>
  );
};

export default LabelUpdateForm;
